import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Banknote,
  Building2,
  ChevronDown,
  CheckCircle,
  Clock,
  Eye,
  EyeOff,
  IndianRupee,
  Minus,
  Plus,
  RefreshCw,
  Save,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/api';

const fmt = (n: number) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  });

const TechnicianWalletPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const technicianId = Number(id);

  const [wallet, setWallet]     = useState<any>(null);
  const [txns, setTxns]         = useState<any[]>([]);
  const [bank, setBank]         = useState<any>(null);
  const [loading, setLoading]   = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage]     = useState(1);
  const [hasMore, setHasMore]   = useState(true);

  // Bank account form
  const [form, setForm] = useState({
    accountHolderName: '',
    accountNumber: '',
    confirmAccountNumber: '',
    ifsc: '',
    bankName: '',
  });
  const [showAcct, setShowAcct] = useState(false);
  const [showConfirmAcct, setShowConfirmAcct] = useState(false);
  const [saving, setSaving] = useState(false);

  // Manual wallet adjustment
  const [adjust, setAdjust] = useState({ amount: '', type: 'CREDIT' as 'CREDIT' | 'DEBIT', notes: '' });
  const [adjusting, setAdjusting] = useState(false);

  // ── load ──────────────────────────────────────────────────────────────

  const loadWallet = useCallback(async () => {
    try {
      const w = await apiService.getAdminWallet(technicianId);
      setWallet(w);
    } catch {
      setWallet(null);
    }
  }, [technicianId]);

  const loadBank = useCallback(async () => {
    try {
      const b = await apiService.getAdminBankAccount(technicianId);
      setBank(b);
    } catch (err: any) {
      if (err?.response?.status === 404) setBank(null);
    }
  }, [technicianId]);

  const loadTxns = useCallback(async (page: number) => {
    setTxLoading(true);
    try {
      const items = await apiService.getAdminWalletTransactions(technicianId, page, 20);
      if (page === 1) setTxns(items ?? []);
      else setTxns(prev => [...prev, ...(items ?? [])]);
      setHasMore((items ?? []).length === 20);
    } catch {
      // silent
    } finally {
      setTxLoading(false);
    }
  }, [technicianId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadWallet(), loadBank(), loadTxns(1)]);
      setLoading(false);
    };
    init();
  }, [loadWallet, loadBank, loadTxns]);

  // ── save bank account ─────────────────────────────────────────────────

  const handleSaveBank = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.accountHolderName.trim() || !form.accountNumber.trim() || !form.ifsc.trim()) {
      toast.error('Account holder name, account number and IFSC are required.');
      return;
    }
    if (form.accountNumber !== form.confirmAccountNumber) {
      toast.error('Account numbers do not match. Please re-enter carefully.');
      return;
    }
    setSaving(true);
    try {
      await apiService.saveAdminBankAccount(technicianId, {
        accountHolderName: form.accountHolderName.trim(),
        accountNumber: form.accountNumber.trim(),
        ifsc: form.ifsc.trim().toUpperCase(),
        bankName: form.bankName.trim(),
      });
      toast.success('Bank account saved successfully.');
      setForm({ accountHolderName: '', accountNumber: '', confirmAccountNumber: '', ifsc: '', bankName: '' });
      await loadBank();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to save bank account.');
    } finally {
      setSaving(false);
    }
  };

  // ── adjust wallet ─────────────────────────────────────────────────────

  const handleAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(adjust.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid amount greater than 0.');
      return;
    }
    if (!adjust.notes.trim()) {
      toast.error('Reason / notes is required.');
      return;
    }
    setAdjusting(true);
    try {
      const res = await apiService.adjustAdminWallet(technicianId, {
        amount,
        type: adjust.type,
        notes: adjust.notes.trim(),
      });
      toast.success(res.message || 'Wallet adjusted successfully.');
      setAdjust({ amount: '', type: 'CREDIT', notes: '' });
      await loadWallet();
      await loadTxns(1);
      setTxPage(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to adjust wallet.');
    } finally {
      setAdjusting(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/technicians')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Technicians
        </button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Wallet className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Technician Wallet</h1>
            <p className="text-sm text-gray-500">ID {technicianId} · balance, bank account &amp; transactions</p>
          </div>
        </div>
        <button
          onClick={async () => { await Promise.all([loadWallet(), loadBank(), loadTxns(1)]); setTxPage(1); }}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Wallet', value: fmt((wallet?.availableBalance ?? 0) + (wallet?.pendingBalance ?? 0)), icon: <Wallet className="h-5 w-5 text-indigo-600" />, bg: 'bg-indigo-50', color: 'text-indigo-700' },
          { label: 'Available (Withdrawable)', value: fmt(wallet?.availableBalance ?? 0), icon: <IndianRupee className="h-5 w-5 text-green-600" />, bg: 'bg-green-50', color: 'text-green-700' },
          { label: 'Pending (T+2 locked)', value: fmt(wallet?.pendingBalance ?? 0), icon: <Clock className="h-5 w-5 text-yellow-600" />, bg: 'bg-yellow-50', color: 'text-yellow-700' },
          { label: 'Total Withdrawn', value: fmt(wallet?.totalWithdrawn ?? 0), icon: <Banknote className="h-5 w-5 text-blue-600" />, bg: 'bg-blue-50', color: 'text-blue-700' },
        ].map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className={`p-2 rounded-lg ${c.bg}`}>{c.icon}</div>
              <span className="text-xs font-medium text-gray-500">{c.label}</span>
            </div>
            <div className={`text-2xl font-black ${c.color}`}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Current bank account */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-bold text-gray-900">Current Bank Account</h2>
          </div>

          {bank ? (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Account Holder', value: bank.accountHolderName },
                { label: 'Bank Name', value: bank.bankName || '-' },
                { label: 'Account No.', value: bank.accountNumber },
                { label: 'IFSC', value: bank.ifsc },
              ].map(f => (
                <div key={f.label} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-400 mb-1">{f.label}</div>
                  <div className="text-sm font-semibold text-gray-900">{f.value || '-'}</div>
                </div>
              ))}
              {bank.isVerified && (
                <div className="col-span-2 flex items-center gap-2 text-green-700 text-xs font-semibold">
                  <CheckCircle className="h-4 w-4" /> Verified
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-sm text-red-700">
              No bank account added yet. Use the form to add one.
            </div>
          )}
        </div>

        {/* Add / update bank account */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Save className="h-5 w-5 text-gray-500" />
            <h2 className="text-sm font-bold text-gray-900">
              {bank ? 'Update Bank Account' : 'Add Bank Account'}
            </h2>
          </div>

          <form onSubmit={handleSaveBank} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Holder Name *</label>
                <input
                  type="text"
                  value={form.accountHolderName}
                  onChange={e => setForm(f => ({ ...f, accountHolderName: e.target.value }))}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Account Number *</label>
                <div className="relative">
                  <input
                    type={showAcct ? 'text' : 'password'}
                    inputMode="numeric"
                    value={form.accountNumber}
                    onChange={e => setForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Enter account number"
                    autoComplete="off"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAcct(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showAcct ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Re-enter Account Number *</label>
                <div className="relative">
                  <input
                    type={showConfirmAcct ? 'text' : 'password'}
                    inputMode="numeric"
                    value={form.confirmAccountNumber}
                    onChange={e => setForm(f => ({ ...f, confirmAccountNumber: e.target.value.replace(/\D/g, '') }))}
                    placeholder="Re-enter account number"
                    autoComplete="off"
                    className={`w-full border rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 ${
                      form.confirmAccountNumber.length > 0
                        ? form.accountNumber === form.confirmAccountNumber
                          ? 'border-green-400 focus:ring-green-300'
                          : 'border-red-400 focus:ring-red-300'
                        : 'border-gray-200 focus:ring-indigo-400'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmAcct(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirmAcct ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.confirmAccountNumber.length > 0 && (
                  <p className={`text-xs mt-1 font-medium ${form.accountNumber === form.confirmAccountNumber ? 'text-green-600' : 'text-red-500'}`}>
                    {form.accountNumber === form.confirmAccountNumber ? '✓ Account numbers match' : '✗ Account numbers do not match'}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">IFSC Code *</label>
                <input
                  type="text"
                  value={form.ifsc}
                  onChange={e => setForm(f => ({ ...f, ifsc: e.target.value.toUpperCase() }))}
                  placeholder="e.g. HDFC0001234"
                  maxLength={11}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={form.bankName}
                  onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))}
                  placeholder="e.g. HDFC Bank"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving
                ? <><RefreshCw className="h-4 w-4 animate-spin" /> Saving...</>
                : <><Save className="h-4 w-4" /> {bank ? 'Update' : 'Save'} Bank Account</>}
            </button>
          </form>
        </div>
      </div>

      {/* Adjust Wallet Balance */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <IndianRupee className="h-5 w-5 text-gray-500" />
          <h2 className="text-sm font-bold text-gray-900">Adjust Wallet Balance</h2>
        </div>

        <form onSubmit={handleAdjust} className="space-y-3">
          {/* Credit / Debit toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAdjust(a => ({ ...a, type: 'CREDIT' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                adjust.type === 'CREDIT'
                  ? 'bg-green-50 border-green-400 text-green-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Plus className="h-4 w-4" /> Add Money (Credit)
            </button>
            <button
              type="button"
              onClick={() => setAdjust(a => ({ ...a, type: 'DEBIT' }))}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                adjust.type === 'DEBIT'
                  ? 'bg-red-50 border-red-400 text-red-700'
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              <Minus className="h-4 w-4" /> Deduct Money (Debit)
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount (₹) *</label>
              <input
                type="number"
                min="1"
                step="0.01"
                value={adjust.amount}
                onChange={e => setAdjust(a => ({ ...a, amount: e.target.value }))}
                placeholder="e.g. 500"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Reason / Notes *</label>
              <input
                type="text"
                value={adjust.notes}
                onChange={e => setAdjust(a => ({ ...a, notes: e.target.value }))}
                placeholder="e.g. Bonus for Dec"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={adjusting}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg disabled:opacity-50 ${
              adjust.type === 'CREDIT' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {adjusting
              ? <><RefreshCw className="h-4 w-4 animate-spin" /> Processing...</>
              : adjust.type === 'CREDIT'
                ? <><Plus className="h-4 w-4" /> Credit ₹{adjust.amount || '0'} to Wallet</>
                : <><Minus className="h-4 w-4" /> Deduct ₹{adjust.amount || '0'} from Wallet</>}
          </button>
        </form>
      </div>

      {/* Transaction history */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-bold text-gray-900">Transaction History</h2>
        </div>

        {txns.length === 0 && !txLoading ? (
          <div className="text-center py-12 text-gray-400 text-sm">No transactions yet.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {txns.map(tx => {
              const isCredit = tx.type?.toLowerCase() === 'credit';
              return (
                <div key={tx.id} className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isCredit ? 'bg-green-50' : 'bg-blue-50'}`}>
                      {isCredit
                        ? <ArrowDownLeft className="h-4 w-4 text-green-600" />
                        : <ArrowUpRight className="h-4 w-4 text-blue-600" />}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {tx.description || (isCredit ? 'Order Credit' : 'Withdrawal')}
                      </div>
                      <div className="text-xs text-gray-400">{fmtDate(tx.createdAt)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-sm font-bold ${isCredit ? 'text-green-600' : 'text-blue-600'}`}>
                      {isCredit ? '+' : '-'}{fmt(tx.amount)}
                    </div>
                    <div className="text-xs text-gray-400 uppercase">{tx.status}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {txLoading && (
          <div className="text-center py-6 text-gray-400 text-sm">Loading...</div>
        )}

        {hasMore && !txLoading && txns.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100">
            <button
              onClick={() => { const next = txPage + 1; setTxPage(next); loadTxns(next); }}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-800 font-medium"
            >
              <ChevronDown className="h-4 w-4" />
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicianWalletPage;
